/**
 * F13.6 — /proformas com 2 linhas de filtros + 4 colunas agregadas.
 *
 * Linha 1: Empresa · Status · Tipo · Busca · "+ Nova proforma"
 * Linha 2: Cliente · Técnico · Período (de/até) · Incluir excluídas
 *
 * Tabela 11 colunas: ☐ · Referência · Tipo · Destinatário · Bruto · Líquido ·
 *   Faturado · Téc. previsto · Téc. facturado · Téc. % · Status · Criada em
 *
 * Filtros persistem em query string (refresh-safe + bookmarkable).
 * Bugfix: badge "fechada" agora usa lime (sucesso); antes era cinza muted.
 * Bugfix: proformas com recipientTechnicianId.isTestFixture=true escondidas
 *         por default (limpa "TechDirect-XXX" da UI).
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";

import { useIsMobile } from "../hooks/useIsMobile.js";
import { useT } from "../i18n/index.js";

import { ErpShell } from "../components/layout/ErpShell.js";
import { BulkActionBar } from "../components/common/BulkActionBar.js";
import {
  CHIP_OFF,
  CHIP_ON,
  FILTER_INPUT,
  FilterField,
  FilterPanel,
} from "../components/common/FilterBar.js";
import { Trash2, X } from "lucide-react";
import { useModals } from "../components/common/ModalsProvider.js";
import { MoneyCell } from "../components/domain/MoneyCell.js";
import { formatMoney } from "../lib/format-fr.js";
import { PercentageCell } from "../components/domain/PercentageCell.js";
import { useAuth } from "../hooks/useAuth.js";
import { useCanEdit } from "../hooks/useCanEdit.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { useBulkSelection } from "../hooks/useBulkSelection.js";
import { trpc } from "../lib/trpc.js";
import { Paginacao } from "../components/common/Paginacao.js";

const PAGE_SIZE = 50;

type RecipientType = "intercompany" | "technician" | "partner";
type ProformaStatus =
  | "draft"
  | "sent"
  | "approved"
  | "rejected"
  | "invoice_received"
  | "paid"
  | "closed"
  | "cancelled";

const STATUS_TONE: Record<ProformaStatus, string> = {
  draft: "bg-neutral-700/40 text-text-secondary",
  sent: "bg-sky-500/15 text-sky-300",
  approved: "bg-lime/15 text-lime",
  rejected: "bg-amber-500/15 text-amber-300",
  invoice_received: "bg-violet-500/15 text-violet-300",
  paid: "bg-lime/20 text-lime",
  // BUGFIX F13.6 — antes era cinza muted; semanticamente é success (proforma
  // encerrada com fatura recebida + pagamento conciliado, fluxo completo).
  closed: "bg-success/15 text-success",
  cancelled: "bg-red-500/15 text-red-300",
};

/** Helpers para sincronizar filtros com query string. */
function parseQuery(search: string) {
  const sp = new URLSearchParams(search);
  return {
    clientId: sp.get("clientId") ? Number(sp.get("clientId")) : null,
    technicianId: sp.get("technicianId") ? Number(sp.get("technicianId")) : null,
    from: sp.get("from") ?? "",
    to: sp.get("to") ?? "",
    deleted: sp.get("deleted") === "1",
  };
}

function buildQuery(params: {
  clientId: number | null;
  technicianId: number | null;
  from: string;
  to: string;
  deleted: boolean;
}): string {
  const sp = new URLSearchParams();
  if (params.clientId != null) sp.set("clientId", String(params.clientId));
  if (params.technicianId != null) sp.set("technicianId", String(params.technicianId));
  if (params.from) sp.set("from", params.from);
  if (params.to) sp.set("to", params.to);
  if (params.deleted) sp.set("deleted", "1");
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export default function Proformas() {
  const t = useT();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const canEdit = useCanEdit();
  const [location] = useLocation();

  const STATUS_LABEL: Record<ProformaStatus, string> = {
    draft: t("proformas.statusDraft"),
    sent: t("proformas.statusSent"),
    approved: t("proformas.statusApproved"),
    rejected: t("proformas.statusRejected"),
    invoice_received: t("proformas.statusInvoiceReceived"),
    paid: t("proformas.statusPaid"),
    closed: t("proformas.statusClosed"),
    cancelled: t("proformas.statusCancelled"),
  };

  const RECIPIENT_LABEL: Record<RecipientType, string> = {
    intercompany: t("proformas.recipientIntercompany"),
    technician: t("proformas.recipientTechnician"),
    partner: t("proformas.recipientPartner"),
  };
  const initialQuery = useMemo(() => {
    // wouter location não inclui search — pegamos de window.location
    return parseQuery(typeof window !== "undefined" ? window.location.search : "");
  }, []);

  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );
  const [companyId, setCompanyId] = useSelectedCompany(accessibleCompanies);

  const [statusFilter, setStatusFilter] = useState<ProformaStatus | "all">("all");
  const [recipientTypeFilter, setRecipientTypeFilter] = useState<RecipientType | "all">("all");
  const [q, setQ] = useState("");
  const [clientFilter, setClientFilter] = useState<number | null>(initialQuery.clientId);
  const [technicianFilter, setTechnicianFilter] = useState<number | null>(
    initialQuery.technicianId,
  );
  const [fromFilter, setFromFilter] = useState(initialQuery.from);
  const [toFilter, setToFilter] = useState(initialQuery.to);
  const [includeDeleted, setIncludeDeleted] = useState(initialQuery.deleted);
  const [offset, setOffset] = useState(0);

  // Sincroniza filtros novos com query string (refresh-safe).
  useEffect(() => {
    const qs = buildQuery({
      clientId: clientFilter,
      technicianId: technicianFilter,
      from: fromFilter,
      to: toFilter,
      deleted: includeDeleted,
    });
    // Path atual sem query
    const pathOnly = location.split("?")[0] ?? "/proformas";
    const next = `${pathOnly}${qs}`;
    if (typeof window !== "undefined" && window.location.search !== qs) {
      // wouter setLocation só recebe o path; usa history API direta para preservar query.
      window.history.replaceState(null, "", next);
    }
  }, [clientFilter, technicianFilter, fromFilter, toFilter, includeDeleted, location]);

  const filters = useMemo(
    () => ({
      companyId: companyId ?? 0,
      status: statusFilter === "all" ? undefined : statusFilter,
      recipientType: recipientTypeFilter === "all" ? undefined : recipientTypeFilter,
      q: q || undefined,
      clientId: clientFilter ?? undefined,
      technicianId: technicianFilter ?? undefined,
      createdFrom: fromFilter || undefined,
      createdTo: toFilter || undefined,
      includeDeleted,
      limit: PAGE_SIZE,
      offset,
    }),
    [
      companyId,
      statusFilter,
      recipientTypeFilter,
      q,
      clientFilter,
      technicianFilter,
      fromFilter,
      toFilter,
      includeDeleted,
      offset,
    ],
  );

  const listQuery = trpc.proformaRequests.list.useQuery(filters, {
    enabled: companyId !== null,
  });
  const clientOptionsQuery = trpc.proformaRequests.filterOptions.clients.useQuery(
    { companyId: companyId ?? 0 },
    { enabled: companyId !== null },
  );
  const technicianOptionsQuery = trpc.proformaRequests.filterOptions.technicians.useQuery(
    { companyId: companyId ?? 0 },
    { enabled: companyId !== null },
  );

  const data = listQuery.data;
  const proformaRows = useMemo(() => data?.rows ?? [], [data?.rows]);
  const selection = useBulkSelection(proformaRows);

  // F12.6b — bulk actions em proformas
  const modals = useModals();
  const utils = trpc.useUtils();
  // F-proforma-pdf-download (CEO 2026-09-30) — PDF direto da lista: abre o
  // guardado no envio ou gera na hora (rascunho). Mesma lógica da tela.
  const generatePdfMutation = trpc.proformaRequests.generatePdf.useMutation();
  const [pdfBusyId, setPdfBusyId] = useState<number | null>(null);
  async function abrirPdf(
    proformaRequestId: number,
    pdfAttachmentId: number | null,
    status: string,
  ) {
    if (companyId == null) return;
    const win = window.open("about:blank", "_blank");
    setPdfBusyId(proformaRequestId);
    try {
      // Enquanto a proforma ainda muda (até ser paga), regera o PDF.
      const regera = ["draft", "sent", "approved", "invoice_received"].includes(status);
      const attachmentId =
        pdfAttachmentId != null && !regera
          ? pdfAttachmentId
          : (await generatePdfMutation.mutateAsync({ companyId, proformaRequestId })).attachmentId;
      const res = await utils.attachments.getDownloadUrl.fetch({ companyId, attachmentId });
      if (win && res?.url) win.location.href = res.url;
      else if (res?.url) window.open(res.url, "_blank", "noopener");
      if (pdfAttachmentId == null) void utils.proformaRequests.list.invalidate();
    } catch (e) {
      win?.close();
      void modals.alert({
        title: t("proformas.pdfErrorTitle"),
        message: `${t("proformas.pdfErrorPre")}${e instanceof Error ? e.message : String(e)}`,
        tone: "danger",
      });
    } finally {
      setPdfBusyId(null);
    }
  }
  function summarize(
    label: string,
    r: {
      updated?: number[];
      sent?: number[];
      skipped: number[];
      errors: { id: number; message: string }[];
    },
  ) {
    const okCount = (r.updated ?? r.sent ?? []).length;
    const parts = [`${label}: ${okCount} ${t("proformas.okSuffix")}`];
    if (r.skipped.length > 0)
      parts.push(
        `${r.skipped.length} ${r.skipped.length === 1 ? t("proformas.skippedOne") : t("proformas.skippedMany")}`,
      );
    if (r.errors.length > 0)
      parts.push(
        `${r.errors.length} ${r.errors.length === 1 ? t("proformas.errorOne") : t("proformas.errorMany")}`,
      );
    void modals.alert({ message: parts.join(" · ") });
  }
  const bulkCancel = trpc.proformaRequests.bulkCancel.useMutation({
    onSuccess: (r) => {
      summarize(t("proformas.summaryCancelled"), r);
      utils.proformaRequests.invalidate();
      selection.clear();
    },
    onError: (e) =>
      void modals.alert({
        title: t("proformas.errorTitle"),
        message: `${t("proformas.errorPrefix")}: ${e.message}`,
        tone: "danger",
      }),
  });
  const bulkApprove = trpc.proformaRequests.bulkApprove.useMutation({
    onSuccess: (r) => {
      summarize(t("proformas.summaryApproved"), r);
      utils.proformaRequests.invalidate();
      selection.clear();
    },
    onError: (e) =>
      void modals.alert({
        title: t("proformas.errorTitle"),
        message: `${t("proformas.errorPrefix")}: ${e.message}`,
        tone: "danger",
      }),
  });
  const bulkResendEmail = trpc.proformaRequests.bulkResendEmail.useMutation({
    onSuccess: (r) => {
      summarize(t("proformas.summaryEmailsSent"), r);
      utils.proformaRequests.invalidate();
      selection.clear();
    },
    onError: (e) =>
      void modals.alert({
        title: t("proformas.errorTitle"),
        message: `${t("proformas.errorPrefix")}: ${e.message}`,
        tone: "danger",
      }),
  });
  const bulkUpdateStatus = trpc.proformaRequests.bulkUpdateStatus.useMutation({
    onSuccess: (r) => {
      summarize(t("proformas.summaryStatusUpdated"), r);
      utils.proformaRequests.invalidate();
      selection.clear();
    },
    onError: (e) =>
      void modals.alert({
        title: t("proformas.errorTitle"),
        message: `${t("proformas.errorPrefix")}: ${e.message}`,
        tone: "danger",
      }),
  });
  const bulkImport = trpc.proformaRequests.bulkImportToRecipient.useMutation({
    onSuccess: (r) => {
      const parts = [
        `${t("proformas.imported")}: ${r.imported.length} ${t("proformas.proformasWord")} → ${r.dossiersCreated} ${t("proformas.dossiersWord")} ${t("proformas.inBS")}`,
      ];
      if (r.skipped.length > 0) parts.push(`${r.skipped.length} ${t("proformas.skippedImport")}`);
      if (r.errors.length > 0) parts.push(`${r.errors.length} ${t("proformas.errorsWord")}`);
      void modals.alert({ message: parts.join(" · ") });
      utils.proformaRequests.invalidate();
      selection.clear();
    },
    onError: (e) =>
      void modals.alert({
        title: t("proformas.errorTitle"),
        message: `${t("proformas.errorPrefix")}: ${e.message}`,
        tone: "danger",
      }),
  });
  const anyBulkPending =
    bulkCancel.isPending ||
    bulkApprove.isPending ||
    bulkResendEmail.isPending ||
    bulkUpdateStatus.isPending ||
    bulkImport.isPending;

  if (!user) return null;
  if (accessibleCompanies.length === 0) {
    return (
      <Shell>
        <p className="text-sm text-text-muted">{t("proformas.noCompanyAccess")}</p>
      </Shell>
    );
  }

  const total = data?.total ?? 0;

  return (
    <Shell>
      {/* Cabeçalho: empresa + ação */}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <FilterField label={t("proformas.companyLabel")}>
          <select
            value={companyId ?? ""}
            onChange={(e) => {
              setCompanyId(Number(e.target.value));
              setOffset(0);
              // Reset filtros dependentes de empresa
              setClientFilter(null);
              setTechnicianFilter(null);
            }}
            className={inputClass + " w-60"}
          >
            {accessibleCompanies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </FilterField>
        {canEdit && (
          <Link
            href="/proformas/nova"
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime px-4 py-1.5 text-sm font-medium"
          >
            {t("proformas.newBtn")}
          </Link>
        )}
      </div>

      {/* Painel de filtros */}
      <FilterPanel testId="proformas-filter-panel">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <FilterField label={t("proformas.searchLabel")} className="sm:col-span-2">
            <input
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setOffset(0);
              }}
              placeholder={t("proformas.searchPlaceholder")}
              className={FILTER_INPUT + " w-full"}
            />
          </FilterField>
          <FilterField label={t("proformas.statusLabel")}>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as ProformaStatus | "all");
                setOffset(0);
              }}
              className={FILTER_INPUT + " w-full"}
            >
              <option value="all">{t("proformas.statusAll")}</option>
              <option value="draft">{t("proformas.statusDraftOpt")}</option>
              <option value="sent">{t("proformas.statusSentOpt")}</option>
              <option value="approved">{t("proformas.statusApprovedOpt")}</option>
              <option value="rejected">{t("proformas.statusRejectedOpt")}</option>
              <option value="invoice_received">{t("proformas.statusInvoiceReceivedOpt")}</option>
              <option value="paid">{t("proformas.statusPaidOpt")}</option>
              <option value="closed">{t("proformas.statusClosedOpt")}</option>
              <option value="cancelled">{t("proformas.statusCancelledOpt")}</option>
            </select>
          </FilterField>
          <FilterField label={t("proformas.recipientLabel")}>
            <select
              value={recipientTypeFilter}
              onChange={(e) => {
                setRecipientTypeFilter(e.target.value as RecipientType | "all");
                setOffset(0);
              }}
              className={FILTER_INPUT + " w-full"}
            >
              <option value="all">{t("proformas.recipientAll")}</option>
              <option value="intercompany">{t("proformas.recipientIntercompany")}</option>
              <option value="technician">{t("proformas.recipientTechnician")}</option>
              <option value="partner">{t("proformas.recipientPartner")}</option>
            </select>
          </FilterField>
          <FilterField label={t("proformas.clientLabel")}>
            <select
              value={clientFilter ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                setClientFilter(v === "" ? null : Number(v));
                setOffset(0);
              }}
              className={FILTER_INPUT + " w-full"}
              title={t("proformas.clientFilterTitle")}
            >
              <option value="">{t("proformas.clientAll")}</option>
              {(clientOptionsQuery.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </FilterField>
          <FilterField label={t("proformas.technicianLabel")}>
            <select
              value={technicianFilter ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                setTechnicianFilter(v === "" ? null : Number(v));
                setOffset(0);
              }}
              className={FILTER_INPUT + " w-full"}
              title={t("proformas.technicianFilterTitle")}
            >
              <option value="">{t("proformas.technicianAll")}</option>
              {(technicianOptionsQuery.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </FilterField>
        </div>

        <div className="flex flex-wrap items-end gap-x-4 gap-y-3 border-t border-[var(--divider)] pt-4">
          <FilterField label={t("proformas.periodLabel")}>
            <div className="flex items-center gap-1.5">
              <input
                type="date"
                value={fromFilter}
                onChange={(e) => {
                  setFromFilter(e.target.value);
                  setOffset(0);
                }}
                className={FILTER_INPUT + " w-full sm:w-36"}
                title={t("proformas.dateFromTitle")}
              />
              <span style={{ color: "var(--text-muted)" }}>→</span>
              <input
                type="date"
                value={toFilter}
                onChange={(e) => {
                  setToFilter(e.target.value);
                  setOffset(0);
                }}
                className={FILTER_INPUT + " w-full sm:w-36"}
                title={t("proformas.dateToTitle")}
              />
            </div>
          </FilterField>

          <div className="flex-1" />

          <label className={(includeDeleted ? CHIP_ON : CHIP_OFF) + " cursor-pointer select-none"}>
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => {
                setIncludeDeleted(e.target.checked);
                setOffset(0);
              }}
              className="accent-lime"
            />
            <Trash2 size={15} aria-hidden="true" />
            {t("proformas.includeDeleted")}
          </label>

          {(clientFilter != null ||
            technicianFilter != null ||
            fromFilter ||
            toFilter ||
            includeDeleted) && (
            <button
              onClick={() => {
                setClientFilter(null);
                setTechnicianFilter(null);
                setFromFilter("");
                setToFilter("");
                setIncludeDeleted(false);
                setOffset(0);
              }}
              className={CHIP_OFF}
            >
              <X size={15} aria-hidden="true" />
              {t("proformas.clearFilters")}
            </button>
          )}
        </div>
      </FilterPanel>

      <div
        className={`rounded-lg border border-neutral-800 bg-neutral-900 ${isMobile ? "overflow-hidden" : "overflow-x-auto"}`}
      >
        {listQuery.isLoading && (
          <p className="p-4 text-sm text-text-muted">{t("proformas.loading")}</p>
        )}
        {listQuery.error && <p className="p-4 text-sm text-red-400">{listQuery.error.message}</p>}
        {data && data.rows.length === 0 && (
          <p className="p-4 text-sm text-text-muted">{t("proformas.emptyState")}</p>
        )}
        {data && data.rows.length > 0 && isMobile && (
          <div data-testid="proformas-cards">
            <label className="flex items-center gap-3 border-b border-neutral-800 px-3 py-2 text-xs text-text-muted">
              <input
                type="checkbox"
                checked={selection.isAllSelected}
                onChange={(e) => (e.target.checked ? selection.selectAll() : selection.clear())}
                className="h-5 w-5 accent-lime"
              />
              {t("proformas.selectCurrentPage")} · {data.rows.length}
            </label>
            <div className="divide-y divide-neutral-800">
              {data.rows.map((p) => {
                const faturado = Number(p.faturado) || 0;
                const liquido = Number(p.netTotal) || 0;
                const tecPrevisto = Number(p.tecPrevisto) || 0;
                const tecFacturado = Number(p.tecFacturado) || 0;
                return (
                  <div
                    key={p.id}
                    data-testid="proforma-card"
                    className={`flex gap-3 px-3 py-3 ${selection.isSelected(p.id) ? "bg-lime/5" : ""} ${
                      (p.status as ProformaStatus) === "cancelled" ? "opacity-50" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selection.isSelected(p.id)}
                      onChange={() => selection.toggle(p.id)}
                      aria-label={p.reference}
                      className="mt-1 h-5 w-5 shrink-0 accent-lime"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <Link
                          href={`/proformas/${p.id}`}
                          className="whitespace-nowrap font-mono text-base font-semibold text-lime hover:text-lime-bright"
                        >
                          {p.reference}
                        </Link>
                        <span
                          className={`inline-block shrink-0 rounded px-2 py-0.5 text-[10px] font-medium ${STATUS_TONE[p.status as ProformaStatus]}`}
                        >
                          {STATUS_LABEL[p.status as ProformaStatus]}
                        </span>
                      </div>
                      <div
                        className="mt-1 break-words text-sm"
                        title={p.recipientName ?? undefined}
                      >
                        {p.recipientName}
                        <span className="text-text-muted">
                          {" "}
                          · {RECIPIENT_LABEL[p.recipientType as RecipientType]}
                        </span>
                      </div>
                      <TecnicosDaProforma p={p} />
                      <div className="mt-1 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs tabular-nums">
                        <span className="text-text-secondary">
                          {new Date(p.createdAt).toLocaleDateString("pt-BR")}
                          {tecPrevisto > 0 && (
                            <>
                              {" "}
                              · {t("proformas.colTecExpected")}{" "}
                              {formatMoney(tecPrevisto, { decimals: 2 })}
                              {tecFacturado > 0 &&
                                ` / ${formatMoney(tecFacturado, { decimals: 2 })}`}
                            </>
                          )}
                        </span>
                        <span className="min-w-0">
                          <span className="text-text-muted">{t("proformas.colNet")}</span>{" "}
                          <b className="font-display text-base">
                            {formatMoney(liquido, { decimals: 2 })}
                          </b>
                          {faturado > 0 && (
                            <>
                              {" "}
                              ·{" "}
                              <span className="text-text-muted">
                                {t("proformas.colInvoiced")}
                              </span>{" "}
                              <b className={faturado >= liquido - 0.01 ? "text-lime" : ""}>
                                {formatMoney(faturado, { decimals: 2 })}
                              </b>
                            </>
                          )}
                        </span>
                      </div>
                      <div className="mt-2 flex justify-end">
                        <button
                          onClick={() => void abrirPdf(p.id, p.pdfAttachmentId ?? null, p.status)}
                          disabled={pdfBusyId != null}
                          className="inline-flex h-10 items-center gap-1 rounded-md border border-neutral-800 px-3 text-xs disabled:opacity-40"
                          title={t("proformas.pdfBtnTitle")}
                        >
                          {pdfBusyId === p.id ? "…" : "📄 PDF"}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {data && data.rows.length > 0 && !isMobile && (
          <table className="w-full text-sm min-w-[1400px]">
            <thead className="bg-neutral-950/50 text-[10px] uppercase tracking-wider text-text-muted">
              <tr>
                <th className="w-10 px-2 py-2">
                  <input
                    type="checkbox"
                    checked={selection.isAllSelected}
                    onChange={(e) => (e.target.checked ? selection.selectAll() : selection.clear())}
                    title={t("proformas.selectCurrentPage")}
                    className="accent-lime"
                  />
                </th>
                <th className="text-left px-2 py-2 w-36">{t("proformas.colReference")}</th>
                <th className="text-left px-2 py-2 w-24">{t("proformas.colType")}</th>
                <th className="text-left px-2 py-2 min-w-[180px]">{t("proformas.colRecipient")}</th>
                <th className="text-right px-2 py-2 w-28">{t("proformas.colGross")}</th>
                <th className="text-right px-2 py-2 w-28">{t("proformas.colNet")}</th>
                <th className="text-right px-2 py-2 w-28" title={t("proformas.colInvoicedTitle")}>
                  {t("proformas.colInvoiced")}
                </th>
                <th
                  className="text-right px-2 py-2 w-28"
                  title={t("proformas.colTecExpectedTitle")}
                >
                  {t("proformas.colTecExpected")}
                </th>
                <th
                  className="text-right px-2 py-2 w-28"
                  title={t("proformas.colTecInvoicedTitle")}
                >
                  {t("proformas.colTecInvoiced")}
                </th>
                <th className="text-right px-2 py-2 w-20" title={t("proformas.colTecPctTitle")}>
                  {t("proformas.colTecPct")}
                </th>
                <th className="text-left px-2 py-2 w-28">{t("proformas.colStatus")}</th>
                <th className="text-left px-2 py-2 w-24">{t("proformas.colCreatedAt")}</th>
                <th className="text-center px-2 py-2 w-16">PDF</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {data.rows.map((p) => {
                const isClosed = (p.status as ProformaStatus) === "closed";
                const isPaid = (p.status as ProformaStatus) === "paid";
                const faturado = Number(p.faturado) || 0;
                const liquido = Number(p.netTotal) || 0;
                const tecPrevisto = Number(p.tecPrevisto) || 0;
                const tecFacturado = Number(p.tecFacturado) || 0;
                // Cor do faturado: lime se completo, branco se parcial, muted se zero
                const faturadoVariant: "lime" | "default" | "muted" =
                  faturado === 0
                    ? "muted"
                    : faturado >= liquido - 0.01 && liquido > 0
                      ? "lime"
                      : "default";
                // Cor do tec_facturado: muted se zero, success se bate o previsto,
                // warning (laranja) só quando há divergência REAL.
                // F-tecfact-tolerance — o técnico fatura um total arredondado e o
                // "previsto" é a soma centavo-a-centavo de (faturado × %), então
                // quase toda fatura diverge por alguns centavos. Tolerância =
                // máx(1 €, 1% do previsto) → ruído de arredondamento fica verde;
                // laranja só em diferença significativa.
                const tecFactTolerance = Math.max(1, Math.abs(tecPrevisto) * 0.01);
                const tecFactVariant: "success" | "warning" | "muted" =
                  tecFacturado === 0
                    ? "muted"
                    : Math.abs(tecFacturado - tecPrevisto) <= tecFactTolerance
                      ? "success"
                      : "warning";
                return (
                  <tr
                    key={p.id}
                    className={`${selection.isSelected(p.id) ? "bg-lime/5" : ""} ${
                      (p.status as ProformaStatus) === "cancelled" ? "opacity-50" : ""
                    } hover:bg-neutral-800/30 transition-colors`}
                  >
                    <td className="px-2 py-2">
                      <input
                        type="checkbox"
                        checked={selection.isSelected(p.id)}
                        onChange={() => selection.toggle(p.id)}
                        className="accent-lime"
                      />
                    </td>
                    <td className="px-2 py-2 font-mono text-xs">
                      <Link
                        href={`/proformas/${p.id}`}
                        className="text-lime hover:text-lime-bright"
                      >
                        {p.reference}
                      </Link>
                    </td>
                    <td className="px-2 py-2 text-xs text-text-secondary">
                      {RECIPIENT_LABEL[p.recipientType as RecipientType]}
                    </td>
                    <td
                      className="px-2 py-2 truncate max-w-[260px]"
                      title={p.recipientName ?? undefined}
                    >
                      {p.recipientName}
                      <TecnicosDaProforma p={p} />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <MoneyCell value={p.grossTotal} variant="muted" decimals={2} />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <MoneyCell value={p.netTotal} variant="default" decimals={2} />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <MoneyCell value={faturado} variant={faturadoVariant} decimals={2} />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <MoneyCell
                        value={tecPrevisto}
                        variant={tecPrevisto === 0 ? "muted" : "default"}
                        decimals={2}
                      />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <MoneyCell value={tecFacturado} variant={tecFactVariant} decimals={2} />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <PercentageCell value={p.tecPctAvg} variant="lime" />
                    </td>
                    <td className="px-2 py-2">
                      <span
                        className={`inline-block rounded px-2 py-0.5 text-[10px] font-medium ${STATUS_TONE[p.status as ProformaStatus]}`}
                      >
                        {STATUS_LABEL[p.status as ProformaStatus]}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-xs text-text-secondary tabular-nums">
                      {new Date(p.createdAt).toLocaleDateString("pt-BR")}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <button
                        onClick={() => void abrirPdf(p.id, p.pdfAttachmentId ?? null, p.status)}
                        disabled={pdfBusyId != null}
                        className="rounded-md border border-neutral-800 hover:border-neutral-700 disabled:opacity-40 px-2 py-1 text-xs"
                        title={t("proformas.pdfBtnTitle")}
                      >
                        {pdfBusyId === p.id ? "…" : "📄"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {total > 0 && (
        <Paginacao
          total={total}
          pageSize={PAGE_SIZE}
          offset={offset}
          onOffset={setOffset}
          noun={total === 1 ? "proforma" : "proformas"}
          testId="proformas-pagination"
        />
      )}

      {/* F12.6b — Barra de ações em massa: aprovar / reenviar e-mail / atualizar
          status / cancelar. Backend valida transições; UI apenas dispara.
          F-roles — escondida para visualizador (somente leitura). */}
      {canEdit && (
        <BulkActionBar
          count={selection.count}
          onClear={selection.clear}
          label={`${selection.count} proforma${selection.count === 1 ? "" : "s"} ${selection.count === 1 ? t("proformas.selectedOne") : t("proformas.selectedMany")}`}
        >
          <button
            onClick={async () => {
              const ok = await modals.confirm({
                title: `${t("proformas.approveVerb")} ${selection.count} ${t("proformas.proformasParenQ")}`,
                message: t("proformas.approveConfirmMsg"),
                confirmLabel: t("proformas.approveVerb"),
              });
              if (!ok) return;
              bulkApprove.mutate({
                companyId: companyId!,
                proformaRequestIds: selection.selectedItems.map((p) => p.id),
              });
            }}
            disabled={anyBulkPending}
            className="rounded-md border border-lime/40 hover:border-lime hover:text-lime text-lime px-3 py-1.5 text-xs disabled:opacity-40"
            title={t("proformas.approveBtnTitle")}
          >
            {t("proformas.approveBtn")}
          </button>
          <button
            onClick={async () => {
              const ok = await modals.confirm({
                title: `${t("proformas.resendEmailOfPre")} ${selection.count} ${t("proformas.proformasParenQ")}`,
                message: t("proformas.resendConfirmMsg"),
                confirmLabel: t("proformas.resendConfirmLabel"),
              });
              if (!ok) return;
              bulkResendEmail.mutate({
                companyId: companyId!,
                proformaRequestIds: selection.selectedItems.map((p) => p.id),
              });
            }}
            disabled={anyBulkPending || selection.count > 200}
            className="rounded-md border border-neutral-700 hover:border-lime hover:text-lime px-3 py-1.5 text-xs disabled:opacity-40"
            title={
              selection.count > 200
                ? t("proformas.resendLimitTitle")
                : t("proformas.resendBtnTitle")
            }
          >
            {t("proformas.resendEmailBtn")}
          </button>
          <button
            onClick={async () => {
              const ok = await modals.confirm({
                title: `${t("proformas.importVerb")} ${selection.count} ${t("proformas.importTitleSuf")}`,
                message: t("proformas.importConfirmMsg"),
                confirmLabel: t("proformas.importConfirmLabel"),
              });
              if (!ok) return;
              bulkImport.mutate({
                companyId: companyId!,
                proformaRequestIds: selection.selectedItems.map((p) => p.id),
              });
            }}
            disabled={anyBulkPending || selection.count > 200}
            className="rounded-md border border-violet-500/40 hover:border-violet-400 hover:text-violet-300 text-violet-300 px-3 py-1.5 text-xs disabled:opacity-40"
            title={
              selection.count > 200
                ? t("proformas.importLimitTitle")
                : t("proformas.importBtnTitle")
            }
          >
            {t("proformas.importBtn")}
          </button>
          <select
            onChange={async (e) => {
              const target = e.target.value as
                | ""
                | "sent"
                | "approved"
                | "rejected"
                | "draft"
                | "paid"
                | "closed";
              e.currentTarget.value = "";
              if (!target) return;
              let reason: string | null = null;
              if (target === "rejected") {
                reason = await modals.promptReason({
                  title: `${t("proformas.rejectVerb")} ${selection.count} ${t("proformas.proformasParen")}`,
                  message: t("proformas.rejectReasonMsg"),
                  placeholder: t("proformas.rejectReasonPlaceholder"),
                  required: true,
                });
                if (!reason) return;
              } else {
                const ok = await modals.confirm({
                  title: `${t("proformas.updateStatusTitlePre")} '${target}' ${t("proformas.updateStatusTitleMid")} ${selection.count} ${t("proformas.proformasParenQ")}`,
                  message: t("proformas.updateStatusConfirmMsg"),
                  confirmLabel: t("proformas.updateStatusConfirmLabel"),
                });
                if (!ok) return;
              }
              bulkUpdateStatus.mutate({
                companyId: companyId!,
                proformaRequestIds: selection.selectedItems.map((p) => p.id),
                targetStatus: target,
                reason: reason ?? undefined,
              });
            }}
            disabled={anyBulkPending}
            defaultValue=""
            className="rounded-md border border-neutral-700 bg-neutral-900 hover:border-sky-500 hover:bg-sky-500/10 hover:text-sky-600 px-3 py-1.5 text-xs disabled:opacity-40"
            title={t("proformas.updateStatusSelectTitle")}
          >
            <option value="">{t("proformas.updateStatusOptDefault")}</option>
            <option value="sent">{t("proformas.updateStatusOptSent")}</option>
            <option value="approved">{t("proformas.updateStatusOptApproved")}</option>
            <option value="rejected">{t("proformas.updateStatusOptRejected")}</option>
            <option value="draft">{t("proformas.updateStatusOptReopen")}</option>
            <option value="paid">{t("proformas.updateStatusOptPaid")}</option>
            <option value="closed">{t("proformas.updateStatusOptClosed")}</option>
          </select>
          <button
            onClick={async () => {
              const reason = await modals.promptReason({
                title: `${t("proformas.cancelVerb")} ${selection.count} ${t("proformas.proformasParen")}`,
                message: t("proformas.cancelReasonMsg"),
                placeholder: t("proformas.cancelReasonPlaceholder"),
                required: true,
                tone: "danger",
                confirmLabel: t("proformas.cancelConfirmLabel"),
              });
              if (!reason) return;
              bulkCancel.mutate({
                companyId: companyId!,
                proformaRequestIds: selection.selectedItems.map((p) => p.id),
                reason,
              });
            }}
            disabled={anyBulkPending}
            className="rounded-md border border-red-500/40 hover:border-red-500 hover:text-red-300 text-red-300 px-3 py-1.5 text-xs disabled:opacity-40"
            title={t("proformas.cancelBtnTitle")}
          >
            {t("proformas.cancelBtn")}
          </button>
        </BulkActionBar>
      )}
    </Shell>
  );
}

const inputClass =
  "rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none disabled:opacity-50";

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <ErpShell>
      <main className="max-w-screen-2xl mx-auto p-6 space-y-4">{children}</main>
    </ErpShell>
  );
}

/**
 * F-proforma-tecnicos-lista (CEO 2026-10-28) — técnico(s) dos itens, quando o
 * destinatário não é o próprio técnico (ES → BS, partner): 1 ou 2 nomes por
 * extenso; 3+ vira "PRIMEIRO +N" com todos no tooltip.
 */
function TecnicosDaProforma({ p }: { p: { recipientType: string; technicianNames?: string[] } }) {
  const names = p.technicianNames ?? [];
  if (p.recipientType === "technician" || names.length === 0) return null;
  const texto = names.length <= 2 ? names.join(", ") : `${names[0]} +${names.length - 1}`;
  return (
    <div
      className="truncate text-xs text-text-muted"
      title={names.join(", ")}
      data-testid="proforma-tecnicos"
    >
      👤 {texto}
    </div>
  );
}
